import React from 'react';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { classNames } from 'primereact/utils';
import { FormField } from '../../FormField/FormField';
import { Progress } from '../../Progress/Progress';
import { Routine } from '../../../types/Fisiofit/routine';
import { DaysRemainingSeverity, RoutineFormFieldProps } from '../types';

type RoutineStatusFieldProps = Pick<RoutineFormFieldProps, 'form'> & {
  currentRoutine: Routine | undefined;
  daysDifference: number | null;
  elapsedDays: number;
  hasStarted: boolean;
  hasEnded: boolean;
  isCreating: boolean;
  isEditing: boolean;
  isNextRoutine: boolean;
  isEndingSoon: boolean;
  daysRemainingSeverity: DaysRemainingSeverity;
  formattedStartDate: string | null;
  formattedEndDate: string | null;
  onFinishRoutine: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

export const RoutineStatusField = ({
  form,
  currentRoutine,
  daysDifference,
  elapsedDays,
  hasStarted,
  hasEnded,
  isCreating,
  isEditing,
  isNextRoutine,
  isEndingSoon,
  daysRemainingSeverity,
  formattedStartDate,
  formattedEndDate,
  onFinishRoutine,
}: RoutineStatusFieldProps) => {
  const renderDaysElapsed = () => {
    if (!form?.duration || daysDifference === null) return <></>;
    if (!hasStarted) {
      return (
        <div className='days-info'>
          <i className='pi pi-stopwatch' />
          Aún no ha comenzado
        </div>
      );
    }

    if (currentRoutine?.isExpired) {
      return (
        <div className='days-info'>
          <span className={classNames('days-elapsed-message', 'error')}>
            <i className={PrimeIcons.EXCLAMATION_CIRCLE} />
            Rutina caducada
          </span>
        </div>
      );
    }

    if (hasEnded) {
      return (
        <div className='days-info'>
          <i className='pi pi-trophy' />
          Rutina terminada
        </div>
      );
    }

    return (
      <div className='days-info'>
        <span className='days-elapsed'>
          {elapsedDays} de {form?.duration} días
        </span>
        {currentRoutine && <Progress color={daysRemainingSeverity} value={(elapsedDays / form?.duration) * 100} showValue />}
      </div>
    );
  };

  return (
    <FormField labelTitle='Estado de la rutina' fullWidth>
      {daysDifference !== null && form?.duration ? (
        <>
          <div className='days-elapsed-container'>{renderDaysElapsed()}</div>

          {!isCreating && isEndingSoon && (
            <div className={classNames('ending-soon-container', daysRemainingSeverity)}>
              <i className={classNames('cell-severity-icon', PrimeIcons.EXCLAMATION_CIRCLE)} />
              <small>
                {form.duration - elapsedDays === 0
                  ? 'Termina hoy'
                  : form.duration - elapsedDays === 1
                    ? 'Termina mañana'
                    : `Termina en ${form.duration - elapsedDays} días`}
              </small>
            </div>
          )}
          <small className='routine-dates'>
            <i className='pi pi-calendar' />
            <span>
              Desde el {formattedStartDate} hasta el {formattedEndDate}
            </span>
          </small>
        </>
      ) : (
        <p>Introduce fecha y duración para ver el estado de la rutina</p>
      )}
      {isEditing && !isNextRoutine && !hasEnded && !currentRoutine?.isExpired && (
        <Button
          className='finish-routine-button'
          label='Finalizar rutina'
          icon={PrimeIcons.POWER_OFF}
          severity='danger'
          text
          onClick={onFinishRoutine}
        />
      )}
    </FormField>
  );
};
